// -----------------------------------------------------------------------------
// A6 PROJECT SETUP — Part 4 (v0.4.0, boards A6, A6.1, A6.2, A6.3)
//   Left:  Cover page (title from the Admin console, photo, client, prepared by, logos) · Action by list
//   Right: Components (name, prefix, drag to reorder, delete when empty) · Disciplines (A6.1 add / delete)
//   Header: 👥 Manage members (A7.1 pop-up) · ✎ Signing page (A6.3) · ⇪ Import report · Cancel · Save changes
// One Save for cover, components and Action by. Disciplines and signatories save in their own pop-ups.
// A prefix change on a component that has issues asks first (A6.2): applied at once by the super admin,
// Administrators, GM and Project head; anyone else with the Project setup switch sends it for approval.
// Every rule is checked again by the database (0010_setup_admins.sql).
// v0.4.1 (round 18 #7): the pop-ups (disciplines, signatories, import, prefix approvals) refresh only their own part —
// unsaved cover / components / Action by edits stay on screen until Save changes or Cancel.
// -----------------------------------------------------------------------------
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { rpc } from "../../../core/api/backend";
import { useRpc } from "../../../core/api/useRpc";
import { href, navigate } from "../../../core/router";
import { useLeaveGuard } from "../../../core/leaveGuard";
import { formatDate } from "../../../core/format";
import { CodeChip, ConfirmSave, DateInput, ErrorBox, InfoIcon, Loading, Modal, PageHeader, toast } from "../../../core/ui/kit";
import { ManageMembersModal } from "../../../core/pages/AdminPage";
import type { Cover, PlansInfo, ProjectSetup, SetupComponent, SetupDiscipline, Signatory } from "../../../core/api/types";
import { PlanUploadModal } from "../components/Plan";
import { GridBadge, GridSetupModal } from "../components/GridSetup";
import { useFileUrl } from "../components/Snapshots";
import { files } from "../../../core/api/backend";
import { uploadCover } from "../../../core/files";
import { ProposeChangeModal } from "../components/ProposeChange";
import { ImportReportModal } from "../components/ImportReport";
import { componentProblems, mergeRows, prefixRenames, readImage, signatoryProblems, DISCIPLINE_COLOURS, MAX_DISCIPLINES, MAX_SIGNATORIES } from "../setup";
import { Icon } from "../../../core/ui/icons";

const TIPS = {
  cover: ["Controls what appears on the report's cover page.", "Project title, Client name and Project owner are set in Admin console › Projects (grey boxes).",
    "Propose change sends a new Client name or Project owner for approval (super admin, Administrators); it shows Pending approval until decided — see Notifications in the profile menu.",
    "A new proposal for the same field replaces your waiting one."],
  coverTitle: ["BIM Issues Tracker + component + version, e.g. GF · Ground floor V03.", "Date of report is chosen when exporting (today or a picked date)."],
  prefix: "Drives the auto-generated issue codes, e.g. GF-01.",
  plan: ["Plan version of the uploaded floor plan PDF (V00, V01…).", "Click it to upload a new version; dashed ⇪ = no plan yet."],
  grid: ["Green tick = grid set up.", "Orange ring = grid in progress (% done).", "Dashed = no plan yet, upload a PDF."],
  // round 22 #4 (board A6 tips 6 + 8): the starting set of a new project comes from SQL app.project_defaults (0012).
  actionBy: ["Used in the resolution log dropdowns.", "A new project starts with BMO; add or remove names as needed."],
  disciplines: ["A new project starts with AR, STR, MEP, CVL, LS and ID.", "Remove the ones a project doesn't use, or add up to 7 in total."],
  colour: ["The 6 default colours are reserved.", "After adding: 7 of 7 active — limit reached."],
  deleteDisc: ["Once no issues are tagged, the discipline is removed straight away with no prompt.", "Issues themselves are never deleted."],
  discCode: ["1–3 capital letters, e.g. AR, STR, MEP.", "Must be unique in the project.", "Codes print in the PDF tables, which have room for 3 letters."],
  signing: ["Printed right after the cover page, in the same style and with the same report number.", "All 8 boxes always print; unused ones stay blank for extra hand-written signatories.", "Leave a date blank to write it in by hand."],
  requests: ["Prefix renames asked for by people who cannot rename straight away.", "Approve re-codes every issue of the component; Reject keeps the prefix."],
};
const errText = (e: unknown) => (e as Error).message;
type Row = SetupComponent & { key: string };
let keySeq = 0;
const toRows = (cs: SetupComponent[]): Row[] => cs.map((c) => ({ ...c, key: c.id ?? `new${keySeq++}` }));
const coverOf = (d: ProjectSetup): Cover => ({ client: d.project.client, prepared_by: d.project.prepared_by, photo: d.project.photo, client_logo: d.project.client_logo, prepared_logo: d.project.prepared_logo, owner_logo: d.project.owner_logo });
const PICTURES = ["photo", "client_logo", "prepared_logo", "owner_logo"] as const;
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function SetupPage({ code }: { code: string }) {
  const { data, error, reload } = useRpc("setup_get", { code });
  if (error) return <div className="page"><ErrorBox message={error} /></div>;
  if (!data) return <Loading />;
  return <SetupForm data={data} code={code} reload={reload} />;
}

function SetupForm({ data, code, reload }: { data: ProjectSetup; code: string; reload: () => void }) {
  const p = data.project;
  const startCover = coverOf(data);
  const [cover, setCover] = useState<Cover>(startCover);
  const [rows, setRows] = useState<Row[]>(() => toRows(data.components));
  const [actionBy, setActionBy] = useState<string[]>(data.action_by);
  const [err, setErr] = useState<string | null>(null);
  const plans = useRpc("plans_get", { code });                            // v0.6.0 Plan column (A6) + A6.7
  const [uploading, setUploading] = useState<string | null>(null);         // component prefix
  const [gridding, setGridding] = useState<string | null>(null);           // v0.7.0 A6.4 Gridline setup of this prefix
  // Fresh data arrived (after Save, or a pop-up refreshed its own part): parts the user has not touched follow the
  // database; edited parts are kept (a component row keeps the fields you changed, the rest follow — e.g. an approved prefix).
  const base = useRef(data);
  useEffect(() => {
    const prev = base.current;
    if (prev === data) return;
    base.current = data;
    setCover((cur) => (same(cur, coverOf(prev)) || same(cur, coverOf(data)) ? coverOf(data) : cur));
    setActionBy((cur) => (same(cur, prev.action_by) || same(cur, data.action_by) ? data.action_by : cur));
    setRows((cur) => mergeRows(cur, prev.components, data.components, toRows) as Row[]);
  }, [data]);
  const [busy, setBusy] = useState(false);
  const [confirmRename, setConfirmRename] = useState(false);
  const [blocked, setBlocked] = useState<Row | null>(null);           // delete a component that has issues
  const [pop, setPop] = useState<"members" | "signing" | "import" | null>(null);
  // v0.7.5: what was just saved shows straight away if the Signing page is reopened before the page has re-read it
  // (found by GitHub's slower test machine: reopening at once showed the old list)
  const [savedSigners, setSavedSigners] = useState<Signatory[] | null>(null);
  useEffect(() => { if (savedSigners && same(data.signatories, savedSigners)) setSavedSigners(null); }, [data, savedSigners]);   // the re-read caught up
  const startRows = useMemo(() => JSON.stringify(data.components.map((c) => [c.id, c.name, c.prefix])), [data]);
  const dirty = JSON.stringify(cover) !== JSON.stringify(startCover) || JSON.stringify(actionBy) !== JSON.stringify(data.action_by)
    || JSON.stringify(rows.map((r) => [r.id, r.name, r.prefix])) !== startRows;
  const renames = prefixRenames(data.components, rows);
  // Leaving with unsaved changes (Esc / Backspace, sidebar, switcher, Back button, tab close) asks first —
  // core/leaveGuard.ts (v0.4.2 Esc only; v0.4.3 every route). "Save" runs the same checks as Save changes.
  useLeaveGuard(dirty, () => {
    const problems = componentProblems(rows);
    if (problems.length) { setErr(problems.join(" · ")); return false; }
    if (renames.length) { setConfirmRename(true); return false; }      // renames need their own pop-up first
    return save();
  });
  const cancel = () => { setCover(startCover); setRows(toRows(data.components)); setActionBy(data.action_by); setErr(null); };
  const trySave = () => {
    const problems = componentProblems(rows);
    if (problems.length) return setErr(problems.join(" · "));
    if (renames.length) return setConfirmRename(true);
    save();
  };
  const save = async (): Promise<boolean> => {
    setConfirmRename(false); setBusy(true); setErr(null);
    try {
      // v0.7.0: cover pictures live in storage. A new picture (data URL) — or one saved before v0.7.0 — is uploaded
      // now and saved as its object name; replaced stored pictures are deleted after the save (Kyle 18/Oct/26).
      const projectId = plans.data?.project_id;
      const stored: Cover = { ...cover };
      if (projectId) for (const k of PICTURES) {
        const v = stored[k];
        if (v && v.startsWith("data:")) stored[k] = await uploadCover(projectId, v);
      }
      const r = await rpc("setup_save", { code, cover: stored, action_by: actionBy,
        components: rows.map((c) => ({ id: c.id, name: c.name.trim(), prefix: c.prefix.trim().toUpperCase() })) });
      for (const k of PICTURES) {
        const old = startCover[k];
        if (old && !old.startsWith("data:") && old !== stored[k]) files.remove(old).catch(() => {});
      }
      setCover(stored);
      toast(r.requested.length ? `Saved. Sent for approval: ${r.requested.map((x) => `${x.old} → ${x.new}`).join(", ")}` : "Saved");
      setBusy(false);
      reload();
      return true;
    } catch (e) { setErr(errText(e)); setBusy(false); return false; }
  };
  return (
    <div className="setup-page">
      <PageHeader crumb={<>{p.name} <CodeChip code={code} /> › Issues Tracker › Setup</>} title="Project setup"
        actions={<>
          <button className="btn" onClick={() => setPop("members")}><Icon name="group" /> Manage members</button>
          <button className="btn" onClick={() => setPop("signing")}><Icon name="edit" /> Signing page</button>
          <button className="btn" onClick={() => setPop("import")}><Icon name="file_upload" /> Import report</button>
          <span className="v-rule" />
          <button className="btn" disabled={!dirty || busy} onClick={cancel}>Cancel</button>
          <button className="btn primary" disabled={!dirty || busy} onClick={trySave}>Save changes</button>
        </>} />
      {err && <ErrorBox message={err} />}
      <div className="setup-grid">
        <div className="setup-col">
          <CoverCard data={data} code={code} cover={cover} setCover={setCover} setErr={setErr} reload={reload} />
          <ActionByCard list={actionBy} setList={setActionBy} />
        </div>
        <div className="setup-col">
          <ComponentsCard rows={rows} setRows={setRows} data={data} code={code} reload={reload} onBlocked={setBlocked} plans={plans.data} onPlan={setUploading} onGrid={setGridding} />
          <DisciplinesCard code={code} list={data.disciplines} reload={reload} />
        </div>
      </div>
      {confirmRename && <RenameModal renames={renames} canRename={data.can_rename} onCancel={() => setConfirmRename(false)} onGo={save} />}
      {blocked && (
        <Modal title={`Can't delete "${blocked.prefix} — ${blocked.name}" yet`} onClose={() => setBlocked(null)} className="small-wide">
          <p className="warn-box"><Icon name="warning" /> {blocked.issues} issues use this component, including voided ones. Issues are never deleted, so the component stays; rename it instead if needed.</p>
          <div className="modal-foot"><a className="btn" href={href({ name: "component", code, prefix: blocked.prefix })}>View its issues</a>
            <button className="btn primary" onClick={() => setBlocked(null)}>OK</button></div>
        </Modal>
      )}
      {pop === "members" && <ManageMembersModal code={code} onClose={() => setPop(null)} onSaved={() => setPop(null)} />}
      {pop === "signing" && <SigningModal code={code} list={savedSigners ?? data.signatories} onClose={() => setPop(null)}
        onSaved={(saved) => { setSavedSigners(saved); setPop(null); toast("Signatories saved"); reload(); }} />}
      {pop === "import" && <ImportReportModal code={code} onClose={() => setPop(null)} onDone={() => { setPop(null); reload(); }} />}
      {gridding && <GridSetupModal code={code} prefix={gridding} onClose={() => setGridding(null)} onSaved={() => { setGridding(null); plans.reload(); }} />}
      {uploading && plans.data && (() => {
        const c = plans.data.components.find((x) => x.prefix === uploading);
        return c ? <PlanUploadModal code={code} prefix={c.prefix} name={c.name} projectId={plans.data.project_id} current={c.current}
          onClose={() => setUploading(null)} onDone={() => { setUploading(null); plans.reload(); }} /> : null;
      })()}
    </div>
  );
}

// ------------------------------------------------------------------ cover page
// Round 30 #9/#10, round 31 #1/#2/#6 (Kyle 30/Oct – 01/Nov/26): Client name and Project owner are read-only here (grey,
// no lock icon) with "Propose change" (A6.8, components/ProposeChange.tsx); a waiting proposal shows "Pending approval".
// Each name sits above its own logo, in the PDF cover's order: Client name + logo, Project owner + logo | Prepared by + logo.
function CoverCard({ data, code, cover, setCover, setErr, reload }: { data: ProjectSetup; code: string; cover: Cover; setCover: (c: Cover) => void; setErr: (e: string | null) => void; reload: () => void }) {
  const set = (patch: Partial<Cover>) => setCover({ ...cover, ...patch });
  const [proposing, setProposing] = useState<"client" | "owner" | null>(null);
  const locked = (field: "client" | "owner", label: string, value: string) => (
    <div className="field" data-locked-field={field}>
      <span className="field-label row between"><span>{label}</span>
        {/* v0.9.2 (round 33 #7, board A6): while a proposal waits, the link itself reads "Pending approval" (orange) — it still
            opens Propose change for a newer value — and the box is outlined orange (v0.9.1 #6); no line under the field */}
        <button type="button" className={`btn link ${data.pending[field] ? "pending-link" : "accent"}`} data-propose={field} data-testid={data.pending[field] ? `pending-${field}` : undefined}
          onClick={() => setProposing(field)}>{data.pending[field] ? "Pending approval" : data.can_approve ? "Change" : "Propose change"}</button></span>
      <input value={value} readOnly className={`locked${data.pending[field] ? " pending" : ""}`} aria-label={label} />
    </div>
  );
  return (
    <section className="card setup-card cover-card" aria-label="Cover page">
      <h2 className="row gap6">Cover page <InfoIcon tip={TIPS.cover} /></h2>
      <p className="cover-auto row gap6"><b>Cover Title : Automatic</b> <InfoIcon tip={TIPS.coverTitle} /></p>
      <label className="field"><span>Project title <span className="faint">set in Admin console</span></span>
        <input value={data.project.name} readOnly className="locked" aria-label="Project title" /></label>
      <ImageField label="Project photo" empty="Upload photo" value={cover.photo} onChange={(v) => set({ photo: v })} setErr={setErr} big />
      <div className="grid2 cover-cols">
        <div className="cover-col">
          {locked("client", "Client name", data.project.client)}
          <ImageField empty="Client logo" value={cover.client_logo} onChange={(v) => set({ client_logo: v })} setErr={setErr} />
          {locked("owner", "Project owner", data.project.owner)}
          <ImageField empty="Project owner logo" value={cover.owner_logo} onChange={(v) => set({ owner_logo: v })} setErr={setErr} />
        </div>
        <div className="cover-col">
          <label className="field"><span>Prepared by</span><input value={cover.prepared_by} onChange={(e) => set({ prepared_by: e.target.value })} aria-label="Prepared by" /></label>
          <ImageField empty="Prepared-by logo" value={cover.prepared_logo} onChange={(v) => set({ prepared_logo: v })} setErr={setErr} />
        </div>
      </div>
      {proposing && <ProposeChangeModal code={code} field={proposing} current={proposing === "client" ? data.project.client : data.project.owner}
        direct={data.can_approve} onClose={() => setProposing(null)} onDone={() => { setProposing(null); reload(); }} />}
    </section>
  );
}

/** Dashed upload box; shows the picture once chosen, × removes it. Pictures are shrunk before saving. */
function ImageField({ label, empty, value, onChange, setErr, big }: {
  label?: string; empty: string; value: string | null; onChange: (v: string | null) => void; setErr: (e: string | null) => void; big?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const stored = useFileUrl(value && !value.startsWith("data:") ? value : null);   // v0.7.0: a picture kept in storage
  const src = value?.startsWith("data:") ? value : stored;
  const pick = async (f: File | undefined) => {
    if (!f) return;
    try { onChange(await readImage(f, big ? 1200 : 600)); setErr(null); } catch (e) { setErr(errText(e)); }
  };
  const box = (
    <div className={`upload-box${value ? " has" : ""}${big ? " big" : ""}`}>
      {value
        ? <><img src={src ?? undefined} alt={label ?? empty} /><button type="button" className="btn icon mini" aria-label={`Remove ${label ?? empty}`} onClick={() => onChange(null)}><Icon name="close" /></button></>
        : <button type="button" className="upload-btn" onClick={() => input.current?.click()}>{empty}</button>}
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden aria-label={`${label ?? empty} file`} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
  return label ? <div className="field"><span>{label}</span>{box}</div> : box;
}

// ------------------------------------------------------------------ action by
function ActionByCard({ list, setList }: { list: string[]; setList: (l: string[]) => void }) {
  const [adding, setAdding] = useState<string | null>(null);
  const add = () => {
    const t = (adding ?? "").trim();
    if (t && !list.some((x) => x.toLowerCase() === t.toLowerCase())) setList([...list, t]);
    setAdding(null);
  };
  return (
    <section className="card setup-card" aria-label="Action by">
      <h2 className="row gap6">Action by <InfoIcon tip={TIPS.actionBy} /></h2>
      <div className="chips" data-testid="action-by">
        {list.map((a) => <span key={a} className="chip">{a}<button type="button" aria-label={`Remove ${a}`} onClick={() => setList(list.filter((x) => x !== a))}><Icon name="close" /></button></span>)}
        {adding === null
          ? <button type="button" className="chip add" onClick={() => setAdding("")}><Icon name="add" /> Add</button>
          : <input className="chip-input" autoFocus value={adding} placeholder="e.g. QS team" aria-label="New Action by"
              onChange={(e) => setAdding(e.target.value)} onBlur={add}
              onKeyDown={(e) => { if (e.key === "Enter") add(); if (e.key === "Escape") { e.stopPropagation(); setAdding(null); } }} />}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ components
function ComponentsCard({ rows, setRows, data, code, reload, onBlocked, plans, onPlan, onGrid }: {
  rows: Row[]; setRows: (r: Row[]) => void; data: ProjectSetup; code: string; reload: () => void; onBlocked: (r: Row) => void;
  plans: PlansInfo | null; onPlan: (prefix: string) => void; onGrid: (prefix: string) => void;
}) {
  const drag = useRef<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const set = (k: string, patch: Partial<Row>) => setRows(rows.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  const add = () => {
    setRows([...rows, { id: null, key: `new${keySeq++}`, prefix: "", name: "", issues: 0 }]);
    setTimeout(() => list.current?.querySelector<HTMLInputElement>(".comp-row:last-child input")?.focus(), 0);
  };
  const drop = (to: number) => {
    const from = drag.current; drag.current = null; setOver(null);
    if (from === null || from === to) return;
    const next = [...rows]; const [m] = next.splice(from, 1); next.splice(to, 0, m); setRows(next);
  };
  const decide = async (id: string, approve: boolean) => {
    try { await rpc("setup_prefix_decide", { code, id, approve }); reload(); } catch (e) { setErr(errText(e)); }
  };
  return (
    <section className="card setup-card comp-card" aria-label="Components">
      <div className="row between"><h2 className="row gap6">Components <span className="count-pill">{rows.length}</span></h2>
        <button type="button" className="btn link accent" onClick={add}><Icon name="add" /> Add component</button></div>
      {data.requests.length > 0 && (
        <div className="requests" data-testid="prefix-requests">
          <p className="mini-label row gap6">Prefix rename requests <InfoIcon tip={TIPS.requests} /></p>
          {data.requests.map((r) => (
            <div key={r.id} className="request">
              <span><b>{r.old_prefix} → {r.new_prefix}</b> · {r.issues} issues · {r.by}, {formatDate(r.on)}</span>
              {data.can_rename
                ? <span className="row gap6"><button className="btn slim-btn" onClick={() => decide(r.id, false)}>Reject</button>
                    <button className="btn slim-btn primary" onClick={() => decide(r.id, true)}>Approve</button></span>
                : <span className="muted small">Waiting for approval</span>}
            </div>))}
        </div>
      )}
      {err && <ErrorBox message={err} />}
      <div className="comp-head"><span /><span>Component</span><span className="row gap6">Prefix <InfoIcon tip={TIPS.prefix} /></span>
        <span className="row gap6">Plan <InfoIcon tip={TIPS.plan} /></span><span className="row gap6">Grid <InfoIcon tip={TIPS.grid} /></span><span /></div>
      <div className="comp-list" ref={list} data-testid="components">
        {rows.map((r, i) => {
          const was = data.components.find((c) => c.id === r.id);
          const renamed = !!was && was.prefix !== r.prefix.trim().toUpperCase();
          return (
            <div key={r.key} className={`comp-row${over === i ? " drop" : ""}`} onDragOver={(e) => { e.preventDefault(); setOver(i); }} onDrop={() => drop(i)}
              onDragLeave={() => setOver((o) => (o === i ? null : o))}>
              <span className="drag" draggable aria-label={`Move ${r.name || "component"}`} title="Drag to reorder"
                onDragStart={(e) => { drag.current = i; e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { drag.current = null; setOver(null); }}>⠿</span>
              <input value={r.name} placeholder="Component name" aria-label={`Component ${i + 1} name`} onChange={(e) => set(r.key, { name: e.target.value })} />
              <input className={`prefix-input${renamed ? " changed" : ""}`} value={r.prefix} maxLength={4} placeholder="XX" aria-label={`Component ${i + 1} prefix`}
                onChange={(e) => set(r.key, { prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} />
              <PlanCell saved={was?.prefix} plans={plans} onPlan={onPlan} /><GridCell saved={was?.prefix} plans={plans} onPlan={onPlan} onGrid={onGrid} />
              <button type="button" className="btn icon mini ghost" aria-label={`Delete ${r.name || "component"}`}
                onClick={() => (r.issues > 0 ? onBlocked(r) : setRows(rows.filter((x) => x.key !== r.key)))}><Icon name="delete" /></button>
            </div>);
        })}
      </div>
    </section>
  );
}

/** A6 Plan column (v0.6.0): the current plan version (click → A6.7 upload a new one) or dashed ⇪ = no plan yet. */
function PlanCell({ saved, plans, onPlan }: { saved: string | undefined; plans: PlansInfo | null; onPlan: (prefix: string) => void }) {
  const c = saved ? plans?.components.find((x) => x.prefix === saved) : undefined;
  if (!c || !plans) return <span className="faint center" title={saved ? undefined : "Save the component first"}>—</span>;
  const can = plans.can_upload;
  return c.current
    ? <button type="button" className="plan-ver" data-plan-chip={`${c.prefix}:${c.current.version}`} disabled={!can} aria-label={`Plan ${c.current.version} of ${c.prefix}${can ? " — upload a new version" : ""}`} onClick={() => onPlan(c.prefix)}>{c.current.version}</button>
    : <button type="button" className="plan-none" data-plan-chip={`${c.prefix}:none`} disabled={!can} aria-label={`Upload a plan for ${c.prefix}`} onClick={() => onPlan(c.prefix)}><Icon name="file_upload" /></button>;
}

/** A6 Grid column (v0.7.0): ✓ set up · orange ring = % labelled · dashed ⇪ = no plan yet (→ A6.7). Click → A6.4 Gridline setup. */
function GridCell({ saved, plans, onPlan, onGrid }: { saved: string | undefined; plans: PlansInfo | null; onPlan: (prefix: string) => void; onGrid: (prefix: string) => void }) {
  const c = saved ? plans?.components.find((x) => x.prefix === saved) : undefined;
  if (!c || !plans) return <span className="faint center">—</span>;
  const can = plans.can_upload;
  return (
    <button type="button" className="grid-cell" data-grid-chip={`${c.prefix}:${c.current ? c.grid.status : "noplan"}`} disabled={!can}
      aria-label={c.current ? `Gridlines of ${c.prefix}` : `Upload a plan for ${c.prefix}`} onClick={() => (c.current ? onGrid(c.prefix) : onPlan(c.prefix))}>
      <GridBadge s={c.grid} hasPlan={!!c.current} />
    </button>);
}

/** A6.2 — rename asks first; approvers rename at once, others send the change for approval. */
function RenameModal({ renames, canRename, onCancel, onGo }: { renames: { from: string; to: string; issues: number }[]; canRename: boolean; onCancel: () => void; onGo: () => void }) {
  const title = renames.length === 1 ? `Rename prefix "${renames[0].from}" → "${renames[0].to}"` : `Rename ${renames.length} prefixes`;
  const n = renames.reduce((s, r) => s + r.issues, 0);
  return (
    <Modal title={title} onClose={onCancel} className="small-wide rename-modal">
      {renames.length > 1 && <ul className="tight">{renames.map((r) => <li key={r.from}>{r.from} → {r.to} ({r.issues} issues)</li>)}</ul>}
      <p className="warn-box red"><Icon name="warning" /> {n} existing issues use {renames.length === 1 ? "this prefix" : "these prefixes"} and will be re-coded.{" "}
        {canRename ? "This action is final." : "This needs approval from admin, GM or project head before it takes effect."}</p>
      <div className="modal-foot"><button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn primary" onClick={onGo}>{canRename ? "Confirm rename" : "Submit for approval"}</button></div>
    </Modal>
  );
}

// ------------------------------------------------------------------ disciplines (A6.1)
function DisciplinesCard({ code, list, reload }: { code: string; list: SetupDiscipline[]; reload: () => void }) {
  const [adding, setAdding] = useState(false);
  const [blocked, setBlocked] = useState<{ d: SetupDiscipline; count: number; codes: string[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const active = list.filter((d) => d.active).length;
  const remove = async (d: SetupDiscipline) => {
    setErr(null);
    try {
      const r = await rpc("setup_discipline_delete", { code, id: d.id });
      if (r.deleted) reload(); else setBlocked({ d, count: r.count ?? 0, codes: r.codes ?? [] });
    } catch (e) { setErr(errText(e)); }
  };
  return (
    <section className="card setup-card" aria-label="Disciplines">
      <div className="row between"><h2 className="row gap6">Disciplines <InfoIcon tip={TIPS.disciplines} /></h2><span className="muted small" data-testid="disc-count">{active} of {MAX_DISCIPLINES} active</span></div>
      {err && <ErrorBox message={err} />}
      <div className="chips" data-testid="disciplines">
        {list.map((d) => <span key={d.id} className="chip disc"><i style={{ background: d.colour }} /><b>{d.code}</b> {d.name}
          <button type="button" aria-label={`Delete discipline ${d.code}`} onClick={() => remove(d)}><Icon name="close" /></button></span>)}
        {active < MAX_DISCIPLINES && <button type="button" className="chip add" onClick={() => setAdding(true)}><Icon name="add" /> Add</button>}
      </div>
      {adding && <AddDisciplineModal code={code} list={list} onClose={() => setAdding(false)} onDone={() => { setAdding(false); reload(); }} />}
      {blocked && (
        <Modal title={`Can't delete "${blocked.d.code} — ${blocked.d.name}" yet`} info={TIPS.deleteDisc} onClose={() => setBlocked(null)} className="small-wide eyebrow-modal">
          <p className="warn-box"><Icon name="warning" /> {blocked.count} issues are still tagged {blocked.d.code} ({blocked.codes.join(", ")}{blocked.count > blocked.codes.length ? ", …" : ""}), including voided ones.
            Re-tag them to another discipline first — bulk edit on the component page is the quickest way.</p>
          <div className="modal-foot"><button className="btn" onClick={() => navigate({ name: "issues", code })}>View tagged issues</button>
            <button className="btn primary" onClick={() => setBlocked(null)}>OK</button></div>
        </Modal>
      )}
    </section>
  );
}

function AddDisciplineModal({ code, list, onClose, onDone }: { code: string; list: SetupDiscipline[]; onClose: () => void; onDone: () => void }) {
  const used = new Set(list.map((d) => d.colour.toUpperCase()));
  const free = DISCIPLINE_COLOURS.filter((c) => !used.has(c));
  const [dcode, setCode] = useState("");
  const [name, setName] = useState("");
  const [colour, setColour] = useState(free[0] ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const close = () => (dcode || name.trim() ? setAsking(true) : onClose());
  useLeaveGuard(!!(dcode || name.trim()));
  const submit = async () => {
    setAsking(false);
    try { await rpc("setup_discipline_add", { code, discipline: { code: dcode, name, colour } }); onDone(); } catch (e) { setErr(errText(e)); }
  };
  return (
    <Modal title="Add discipline" onClose={close} className="small-wide">
      <div className="disc-form">
        <label className="field"><span className="field-label">Code <InfoIcon tip={TIPS.discCode} /></span><input autoFocus value={dcode} maxLength={3} placeholder="FP" aria-label="Discipline code" onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))} /></label>
        <label className="field"><span>Name</span><input value={name} placeholder="Fire Protection" aria-label="Discipline name" onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }} /></label>
      </div>
      <div className="field"><span className="field-label">Colour <InfoIcon tip={TIPS.colour} /></span>
        <div className="swatches" role="radiogroup" aria-label="Colour">
          {DISCIPLINE_COLOURS.map((c) => <button key={c} type="button" role="radio" aria-checked={colour === c} aria-label={c} disabled={used.has(c)}
            className={`swatch${colour === c ? " on" : ""}`} style={{ background: c }} onClick={() => setColour(c)} />)}
        </div>
      </div>
      {err && <ErrorBox message={err} />}
      <div className="modal-foot"><button className="btn" onClick={close}>Cancel</button><button className="btn primary" onClick={submit}>Add discipline</button></div>
      {asking && <ConfirmSave onSave={submit} onDiscard={() => { setAsking(false); onClose(); }} onKeep={() => setAsking(false)} text="What you typed here has not been saved." />}
    </Modal>
  );
}

// ------------------------------------------------------------------ signing page (A6.3)
function SigningModal({ code, list, onClose, onSaved }: { code: string; list: Signatory[]; onClose: () => void; onSaved: (saved: Signatory[]) => void }) {
  type SRow = Signatory & { k: number };
  const nextK = useRef(0);
  const blank = (): SRow => ({ name: "", title: "", company: "", sign_date: null, k: nextK.current++ });
  const [rows, setRows] = useState<SRow[]>(() => (list.length ? list.map((r) => ({ ...r, k: nextK.current++ })) : [blank()]));
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [badDate, setBadDate] = useState<Set<number>>(new Set());     // row keys whose typed date can't be read
  const [problems, setProblems] = useState<{ row: number; text: string }[]>([]);
  // v0.7.4: every change works on the latest rows (a late update from a date field can't bring back an older list)
  const set = (i: number, patch: Partial<Signatory>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const dateOk = (i: number, ok: boolean) => setBadDate((b) => { const n = new Set(b); if (ok) n.delete(i); else n.add(i); return same([...n], [...b]) ? b : n; });
  const remove = (i: number) => { const k = rows[i].k; setRows((rs) => rs.filter((r) => r.k !== k)); dateOk(k, true); setProblems([]); };
  // Save stops with one line per problem row — nothing is dropped or ignored silently (round 18 #3, bug v040-010)
  const save = async () => {
    const found = signatoryProblems(rows, new Set(rows.flatMap((r, i) => (badDate.has(r.k) ? [i] : []))));
    setProblems(found);
    if (found.length) return;
    const kept = rows.filter((r) => r.name.trim() || r.title.trim() || r.company.trim() || r.sign_date).map(({ k: _k, ...r }) => r);
    try { await rpc("setup_signatories_save", { code, signatories: kept }); onSaved(kept); }
    catch (e) { setErr(errText(e)); }
  };
  const badRow = (i: number) => problems.some((p) => p.row === i + 1);
  // Closing with changes (×, Esc, a click outside — e.g. on the page's Save changes — or Cancel) asks first,
  // like the Manage members pop-up: what was typed is never dropped silently (v0.4.2, bug v041-001).
  const strip = (rs: Signatory[]) => JSON.stringify(rs.map((r) => [r.name, r.title, r.company, r.sign_date]));
  const dirty = badDate.size > 0 || strip(rows) !== strip(list.length ? list : [{ name: "", title: "", company: "", sign_date: null }]);
  const [asking, setAsking] = useState(false);
  const close = () => (dirty ? setAsking(true) : onClose());
  useLeaveGuard(dirty);
  return (
    <Modal title="Signing page" info={TIPS.signing} onClose={close} className="signing-modal natural">
      <div className="sign-grid sign-head"><span /><span>Name</span><span>Title</span><span>Company name</span><span>Date</span><span /></div>
      <div data-testid="signatories">
        {rows.map((r, i) => (
          <div key={r.k} className={`sign-grid${badRow(i) ? " bad" : ""}`} data-row={i + 1}>
            <span className="faint">{i + 1}</span>
            <input value={r.name} className={problems.some((p) => p.row === i + 1 && p.text.includes("name")) ? "invalid" : ""} aria-label={`Signatory ${i + 1} name`} onChange={(e) => set(i, { name: e.target.value })} />
            <input value={r.title} aria-label={`Signatory ${i + 1} title`} onChange={(e) => set(i, { title: e.target.value })} />
            <input value={r.company} aria-label={`Signatory ${i + 1} company`} onChange={(e) => set(i, { company: e.target.value })} />
            <DateInput value={r.sign_date} ariaLabel={`Signatory ${i + 1} date`} onChange={(v) => set(i, { sign_date: v })} onValidity={(ok) => dateOk(r.k, ok)} invalid={badDate.has(r.k) && badRow(i)} />
            <button type="button" className="btn icon mini ghost" aria-label={`Remove signatory ${i + 1}`} onClick={() => remove(i)}><Icon name="delete" /></button>
          </div>))}
      </div>
      <div className="row between">
        {rows.length < MAX_SIGNATORIES ? <button type="button" className="btn link accent" onClick={() => setRows((rs) => [...rs, blank()])}><Icon name="add" /> Add signatory</button> : <span />}
        <span className="muted small">{rows.length} of {MAX_SIGNATORIES}</span>
      </div>
      {problems.length > 0 && <div className="error-box sign-errors" role="alert" data-testid="sign-errors">
        {problems.map((p, k) => <div key={k}>Row {p.row}: {p.text}</div>)}</div>}
      {err && <ErrorBox message={err} />}
      <div className="modal-foot sep"><button className="btn" onClick={() => setPreview(true)}>Preview page</button><span className="grow" />
        <button className="btn" onClick={close}>Cancel</button><button className="btn primary" onClick={save}>Save signatories</button></div>
      {preview && <SigningPreview rows={rows} onClose={() => setPreview(false)} />}
      {asking && <ConfirmSave onSave={() => { setAsking(false); save(); }} onDiscard={() => { setAsking(false); onClose(); }} onKeep={() => setAsking(false)} text="What you typed here has not been saved." />}
    </Modal>
  );
}

/** What the printed signing page (P1.1) will hold: 8 boxes, unused ones blank. Portrait sheet, 2 across × 4 down (round 22). */
function SigningPreview({ rows, onClose }: { rows: Signatory[]; onClose: () => void }) {
  const boxes: (Signatory | null)[] = [...rows, ...Array(MAX_SIGNATORIES).fill(null)].slice(0, MAX_SIGNATORIES);
  return (
    <Modal title="Signing page · preview" onClose={onClose} className="sign-preview">
      <div className="sign-sheet">{boxes.map((s, i) => <SignBox key={i} s={s} />)}</div>
    </Modal>
  );
}
const SignBox = ({ s }: { s: Signatory | null }): ReactNode => (
  <div className="sign-box"><div className="sign-line" />
    <p><b>{s?.name || " "}</b></p><p>{s?.title || " "}</p><p>{s?.company || " "}</p><p className="muted">Date: {s?.sign_date ? formatDate(s.sign_date) : "__________"}</p></div>
);

